/**
 * Pure helpers for the Admin · Users & Roles section (#938).
 *
 * Extracted from `admin-users-and-roles.section.ts` so they can be unit-
 * tested without an Angular harness. Marketing-UI doesn't yet ship its
 * own Jest config — these helpers are imported by tests living under
 * `packages/Marketing/src/__tests__/utils/` (same convention as
 * `role-filter.ts`).
 *
 * No Angular / framework deps allowed in this file.
 */

import type { marketingAuthorizedUserEntity } from 'mj_generatedentities';

export type RoleTierLocal = 'Admin' | 'Operator' | 'Viewer';
export const ROLE_OPTIONS: ReadonlyArray<RoleTierLocal> = ['Admin', 'Operator', 'Viewer'];

export interface UserOption {
    ID: string;
    Name: string;
    Email: string;
}

export interface AuthorizedUserRow {
    entity: marketingAuthorizedUserEntity;
    id: string;
    userId: string;
    userName: string;
    userEmail: string;
    role: RoleTierLocal;
}

/** Type guard — runtime check that an unknown value is a valid Role tier. */
export function isRole(value: unknown): value is RoleTierLocal {
    return value === 'Admin' || value === 'Operator' || value === 'Viewer';
}

/**
 * Project an entity + the loaded user list into a presentation row.
 * Pure: no DB / framework calls. Falls back to 'Operator' when the
 * entity surfaces no Role (the v1 manually-managed view shape, before
 * V202605041207). The fallback keeps the dropdown wired even if the
 * server has not yet replayed the view migration — but in that case the
 * GraphQL non-null on `marketingAuthorizedUser_.Role` will throw before
 * we ever reach this projection, so the fallback is defense-in-depth.
 */
export function toAuthorizedUserRow(
    entity: marketingAuthorizedUserEntity,
    allUsers: ReadonlyArray<UserOption>,
): AuthorizedUserRow {
    const rawRole = entity.Role as RoleTierLocal | null | undefined;
    const role: RoleTierLocal = isRole(rawRole) ? rawRole : 'Operator';
    const userId = entity.UserID;
    const denormName = entity.User || '';
    const lookupUser = allUsers.find(u => u.ID.toLowerCase() === userId.toLowerCase());
    return {
        entity,
        id: entity.ID,
        userId,
        userName: lookupUser?.Name || denormName || '',
        userEmail: lookupUser?.Email || '',
        role,
    };
}

/**
 * Filter the User list down to those NOT yet on the AuthorizedUser
 * allow-list for this company. Used to populate the grant-access dropdown.
 */
export function grantableUsers(
    allUsers: ReadonlyArray<UserOption>,
    rows: ReadonlyArray<{ userId: string }>,
): UserOption[] {
    const taken = new Set(rows.map(r => r.userId.toLowerCase()));
    return allUsers.filter(u => !taken.has(u.ID.toLowerCase()));
}
